import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  BOOTSTRAP_COMPANY_REGISTRATION,
  RESEND_COMPANY_REGISTRATION_VERIFICATION,
  SUBMIT_COMPANY_REGISTRATION,
  VERIFY_COMPANY_REGISTRATION,
} from '../graphql/mutations';
import type {
  MutationOutcome,
  SubmitCompanyRegistrationInput,
} from '../types/companyRegistration.types';

/**
 * The applicant-side registration writes as `{ run, loading }`-style runners. Business failures
 * (duplicate guards, OTP failures, state-machine rules) come back in the payload's `errors` array —
 * checked before assuming success (§5.2/§9.2), never thrown.
 */
export function useCompanyRegistrationMutations() {
  // The authenticated journey lands the applicant in the Company Portal immediately, and the portal
  // gate reads `myCompanyRegistration` — so that query must be refreshed before the navigation or the
  // guard sees no company journey and bounces to onboarding. A no-op on the anonymous journey, where
  // the query is skipped and Apollo only refetches queries that are actually active.
  const [submitM, { loading: submitting }] = useMutation(SUBMIT_COMPANY_REGISTRATION, {
    refetchQueries: ['MyCompanyRegistration'],
    awaitRefetchQueries: true,
  });
  const [verifyM, { loading: verifying }] = useMutation(VERIFY_COMPANY_REGISTRATION);
  const [resendM, { loading: resending }] = useMutation(RESEND_COMPANY_REGISTRATION_VERIFICATION);
  const [bootstrapM, { loading: bootstrapping }] = useMutation(BOOTSTRAP_COMPANY_REGISTRATION, {
    refetchQueries: ['MyCompanyRegistration'],
  });

  const submitRegistration = useCallback(
    async (input: SubmitCompanyRegistrationInput): Promise<MutationOutcome> => {
      const res = await submitM({ variables: { input } });
      const payload = res.data?.submitCompanyRegistration;
      const errors = payload?.errors ?? [];
      return { ok: errors.length === 0, errors, registration: payload?.companyRegistration };
    },
    [submitM],
  );

  const verifyRegistration = useCallback(
    async (registrationId: string, otp: string): Promise<MutationOutcome> => {
      const res = await verifyM({ variables: { input: { registrationId, otp } } });
      const payload = res.data?.verifyCompanyRegistration;
      const errors = payload?.errors ?? [];
      return { ok: errors.length === 0, errors, registration: payload?.companyRegistration };
    },
    [verifyM],
  );

  /**
   * Requests a fresh verification code by registration id, or by corporate email for an applicant who
   * no longer has the verify link. The outcome is deliberately uninformative — the server answers
   * identically whether the registration is pending, expired or absent, and says which in the email —
   * so callers must not present it as confirmation that anything was sent.
   */
  const resendVerification = useCallback(
    async (key: { registrationId: string } | { email: string }): Promise<MutationOutcome> => {
      const res = await resendM({ variables: { input: key } });
      const errors = res.data?.resendCompanyRegistrationVerification.errors ?? [];
      return { ok: errors.length === 0, errors };
    },
    [resendM],
  );

  const bootstrapRegistration = useCallback(
    async (registrationId: string): Promise<MutationOutcome> => {
      const res = await bootstrapM({ variables: { input: { registrationId } } });
      const payload = res.data?.bootstrapCompanyRegistration;
      const errors = payload?.errors ?? [];
      return { ok: errors.length === 0, errors, registration: payload?.companyRegistration };
    },
    [bootstrapM],
  );

  return {
    submitRegistration,
    verifyRegistration,
    resendVerification,
    bootstrapRegistration,
    submitting,
    verifying,
    resending,
    bootstrapping,
  };
}
